import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import FormatUrl from "../../utils/UrlFormatter";
import StandardModal from '../Modals/standardModal';
import './staffManage.css';

const RemovePractitioner = ({ practitioners, onRemovePractitioner, clinicId }) => {
  const { t } = useTranslation();

  const [selectedDiscipline, setSelectedDiscipline] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [selectedFirstName, setSelectedFirstName] = useState('');
  const [selectedLastName, setSelectedLastName] = useState('');
  const [errors, setErrors] = useState({});
  const [loadingDisciplines, setLoadingDisciplines] = useState(false);

  // Modal state
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);


  const closeModal = () => {
    setIsModalVisible(false);
    setModalTitle('');
    setModalMessage('');
    if (modalTitle === "Success") {
      onRemovePractitioner(selectedId); // Only navigate on success
  }
  };

  const handleDisciplineChange = (e) => {
    const newDiscipline = e.target.value;
    setLoadingDisciplines(true); 
    setSelectedDiscipline(newDiscipline);
    resetFilters();
  
    // simulate async delay or wait for state update
    setTimeout(() => {
      setLoadingDisciplines(false);
    }, 150); 
  };
  

  const resetFilters = () => {
    setSelectedId('');
    setSelectedFirstName('');
    setSelectedLastName('');
    setErrors({});
  };

  // Unique filtering options
  const uniqueDisciplines = [...new Set(practitioners.map((p) => p.discipline))];
  const uniqueIds = [...new Set(
    practitioners
      .filter((p) => selectedDiscipline === '' || p.discipline === selectedDiscipline)
      .map((p) => p.id.toString())
  )];

  const uniqueFirstNames = [...new Set(
    practitioners
      .filter((p) => selectedDiscipline === '' || p.discipline === selectedDiscipline)
      .map((p) => p.firstName)
  )];

  const uniqueLastNames = [...new Set(
    practitioners
      .filter((p) => selectedDiscipline === '' || p.discipline === selectedDiscipline)
      .map((p) => p.lastName)
  )];

  // Handle practitioner selection by ID
  const handleSelectChange = (e) => {
    setSelectedId(e.target.value);
    const practitioner = practitioners.find((p) => p.id.toString() === e.target.value);
    if (practitioner) {
      setSelectedFirstName(practitioner.firstName);
      setSelectedLastName(practitioner.lastName);
    }
  };

  // Handle name-based practitioner selection
  const handleNameChange = () => {
    const practitioner = practitioners.find(
      (p) =>
        p.firstName === selectedFirstName &&
        p.lastName === selectedLastName &&
        (selectedDiscipline === '' || p.discipline === selectedDiscipline)
    );

    if (practitioner) {
      setSelectedId(practitioner.id.toString());
    }
  };

  
  const handleRemove = async () => {
    if (!validate()) return;

    const practitionerToRemove = practitioners.find((p) => p.id.toString() === selectedId);
  
    if (!practitionerToRemove) {
      setModalTitle(t("staff_remove.practitioner_not_found"));
      setModalMessage(t("staff_remove.practitioner_not_found_message"));
      setIsModalVisible(true);
      return;
    }

    const { id } = practitionerToRemove;

    if (!id) {
      console.error("Profile ID is missing for selected practitioner:", practitionerToRemove);
      setModalTitle(t("staff_remove.error"));
      setModalMessage(t("staff_remove.profile_id_missing"));
      setIsModalVisible(true);
      return;
    }

    try {
    
      const csrfUrl = FormatUrl(`/csrf-token/`);
      const csrfResponse = await fetch(csrfUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!csrfResponse.ok) {
        console.error("Failed to fetch CSRF token:", csrfResponse.status);
        throw new Error("Failed to fetch CSRF token");
      }

      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;

      // const payload = {
      //   clinic_id: clinicId,
      //   practitioner_id: id,
      // };
      const formData = new URLSearchParams();

        formData.append('clinic_id', clinicId);  
        formData.append('practitioner_id', id);

        const response = await fetch(FormatUrl("/deactivate-doctor/"), {
        method: "POST",
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
        body: formData,
      });

      const data = await response.json();;

      if (response.ok && data.status === "Success") {
        // onRemovePractitioner(selectedId);
        setModalTitle(t("staff_remove.success"));
        setModalMessage(t("staff_remove.practitioner_deactivated"));
        setIsModalVisible(true);
        resetFilters();
      } else {
        console.error("Failed to deactivate practitioner. Error:", data.error);
        setModalTitle(t("staff_remove.error"));
        setModalMessage(data.error || (t("staff_remove.deactivation_failed")));
        setIsModalVisible(true);
      }
    } catch (error) {
      console.error("Error removing practitioner:", error);
      setModalTitle(t("staff_remove.error"));
      setModalMessage(t("staff_remove.generic_error"));
      setIsModalVisible(true);
    }
  };


  
  // Validation
  const validate = () => {
    let valid = true;
    const newErrors = {};

    if (!selectedId) {
      newErrors.selectedId = "Please select a practitioner by ID";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const selectedPractitioner = practitioners.find((p) => p.id.toString() === selectedId);

  
  return (
    <div className="remove-practitioner-container">
      <h3 className="add-practitioner-title">{t("staff_remove.remove_practitioner")}</h3>

      {/* Filters Section */}
      <div className="filters-section">
        <h4>{t("staff_remove.filters")}</h4>
        <select value={selectedDiscipline} onChange={handleDisciplineChange}>
          <option value="">{t("staff_remove.all_disciplines")}</option>
          {loadingDisciplines ? (
            <option disabled>{t("staff_remove.loading_disciplines")}</option>
          ) : (
            uniqueDisciplines.map((discipline, index) => (
              <option key={index} value={discipline}>
                {discipline}
              </option>
            ))
          )}
        </select>
        
        <select value={selectedId} onChange={handleSelectChange} disabled={!selectedDiscipline}>
          <option value="" disabled>{t("staff_remove.select_practitioner_id")}</option>
          {uniqueIds.map((id, index) => (
            <option key={index} value={id}>
              {id}
            </option>
          ))}
        </select>

        <div className="name-fields">
          <select
            value={selectedFirstName}
            onChange={(e) => setSelectedFirstName(e.target.value)}
            disabled={!selectedDiscipline}
          >
            <option value="">{t("staff_remove.all_first_names")}</option>
            {uniqueFirstNames.map((firstName, index) => (
              <option key={index} value={firstName}>
                {firstName}
              </option>
            ))}
          </select>

          <select
            value={selectedLastName}
            onChange={(e) => setSelectedLastName(e.target.value)}
            disabled={!selectedDiscipline}
          >
            <option value="">{t("staff_remove.all_last_names")}</option>
            {uniqueLastNames.map((lastName, index) => (
              <option key={index} value={lastName}>
                {lastName}
              </option>
            ))}
          </select>
        </div>

        <button type="button" className='load-btn' onClick={handleNameChange} disabled={!selectedFirstName || !selectedLastName}>
        {t("staff_remove.load_practitioner_by_name")}
        </button>
      </div>

      {/* Practitioner Card Section */}
      <div className="updating-fields">
        <h4>{t("staff_remove.practitioner_details")}</h4>
        {selectedPractitioner ? (
          <div className="practitioner-card">
            <div className="card-content">
              <h4>{`${selectedPractitioner.firstName}  ${selectedPractitioner.lastName}`}</h4>
              <p><strong>{t("staff_remove.id")}:</strong> {selectedPractitioner.id}</p>
              <p><strong>{t("staff_remove.email")}:</strong> {selectedPractitioner.email}</p>
              <p><strong>{t("staff_remove.phone")}:</strong> {selectedPractitioner.phone}</p>
              <p><strong>{t("staff_remove.discipline")}:</strong> {selectedPractitioner.discipline}</p>
              {/* <p><strong>Specialization:</strong> {selectedPractitioner.specialization}</p> */}
              <p><strong>{t("staff_remove.license_number")}:</strong> {selectedPractitioner.licenseNumber}</p>
              {/* <p><strong>License Expiry:</strong> {new Date(selectedPractitioner.licenseExpiry).toLocaleDateString()}</p>
              <p><strong>Years of Experience:</strong> {selectedPractitioner.yearsOfExperience}</p>
              <p><strong>Status:</strong> {selectedPractitioner.verificationStatus}</p> */}
            </div>
          </div>
        ) : (
          <p>{t("staff_remove.no_practitioner_selected")}</p>
        )}
      </div>

      {errors.selectedId && <p className="error">{errors.selectedId}</p>}
      <button type="submit" className="remove-btn" onClick={handleRemove}>
      {t("staff_remove.remove_practitioner")}
      </button>

      {/* Modal Component */}
      <StandardModal
        isOpen={isModalVisible}
        onClose={closeModal}
        title={modalTitle}
        message={modalMessage}
      />

    </div>
  );
};

export default RemovePractitioner;
